// How live objects move through the room without passing through it: the
// path a device flies between two seats, and how far a selected device is
// held out so its turn clears what stands behind it. Both ask the room's
// clearance map; neither knows what the obstacles are.

import * as THREE from "three";
import { CELL, lattice, type Clearance } from "./clearance.ts";

/** How far a selected device turns to either side, in radians. */
export const SWAY = 0.26;

/** Where a device stands: its root's point, the way it faces, and its bounds there in the root's frame. */
export interface Seat {
  position: THREE.Vector3;
  yaw: number;
  box: THREE.Box3;
}

/** The sample points of a device whose box is `box`, standing at `position` facing `yaw`, held `hold` off its rest. */
export function standing(box: THREE.Box3, position: THREE.Vector3, yaw: number, hold = new THREE.Vector3()): THREE.Vector3[] {
  const turn = new THREE.Quaternion().setFromAxisAngle(THREE.Object3D.DEFAULT_UP, yaw);
  return lattice(box).map((point) => point.add(hold).applyQuaternion(turn).add(position));
}

/**
 * What a device standing on `seat` may touch without it counting: whatever
 * its sample points touch there, and the top of the surface it stands on for
 * a little way around, so that leaving its own shelf, or the low lip of its
 * stand, is not a collision. The allowance is one cell deep: the underside
 * of a board the device stands on still blocks.
 */
export function resting(seat: Seat, clearance: Clearance): Set<number> {
  const allowed = clearance.touching(standing(seat.box, seat.position, seat.yaw));
  const foot = seat.position.clone();
  foot.y += seat.box.min.y;
  for (const cell of clearance.slab(foot, 0.4, CELL, CELL * 3)) allowed.add(cell);
  return allowed;
}

/** How far out of each end a flight first travels, and how high, tried from the gentlest arc up. */
const ARCS: Array<[pull: number, rise: number]> = [];
for (const pull of [0.1, 0.18, 0.28, 0.4, 0.55]) {
  // A device is lifted out and set down: it never slides in low.
  for (const rise of [0.1, 0.18, 0.28, 0.4, 0.55]) if (rise >= pull * 0.6) ARCS.push([pull, rise]);
}
ARCS.sort((a, b) => a[0] + a[1] - (b[0] + b[1]));

/**
 * A path from one seat to another through the room's free space. A device
 * leaves a seat the way it faces, out and up, and comes to the next one from
 * the same side; the curve between is the gentlest one on which the device
 * touches nothing but what it touches at rest in the two seats. Without a
 * clearance map the gentlest curve is used as is.
 */
export function route(
  from: Seat, to: Seat, clearance: Clearance | null,
  /** The device's bounds part of the way through, in its root's frame. Without it the two seats' bounds are blended. */
  boxAt?: (t: number) => THREE.Box3,
): (t: number, into: THREE.Vector3) => THREE.Vector3 {
  const out = (yaw: number) => new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
  const curve = ([pull, rise]: [number, number]) => {
    const a = from.position.clone().addScaledVector(out(from.yaw), pull).add(new THREE.Vector3(0, rise, 0));
    const b = to.position.clone().addScaledVector(out(to.yaw), pull).add(new THREE.Vector3(0, rise, 0));
    return (t: number, into: THREE.Vector3) => {
      const u = 1 - t;
      return into.set(0, 0, 0)
        .addScaledVector(from.position, u * u * u).addScaledVector(a, 3 * u * u * t)
        .addScaledVector(b, 3 * u * t * t).addScaledVector(to.position, t * t * t);
    };
  };
  if (!clearance) return curve(ARCS[0]!);
  const allowed = resting(from, clearance);
  for (const cell of resting(to, clearance)) allowed.add(cell);
  const at = new THREE.Vector3();
  const box = new THREE.Box3();
  // Close enough together that the device moves less than a cell between two.
  const samples = Math.max(24, Math.ceil(from.position.distanceTo(to.position) / CELL));
  // The device's own shape along the way does not depend on the arc.
  const shapes: THREE.Vector3[][] = [];
  for (let i = 1; i < samples; i++) {
    const t = i / samples;
    if (boxAt) box.copy(boxAt(t));
    else {
      box.min.lerpVectors(from.box.min, to.box.min, t);
      box.max.lerpVectors(from.box.max, to.box.max, t);
    }
    shapes.push(standing(box, new THREE.Vector3(), THREE.MathUtils.lerp(from.yaw, to.yaw, t)));
  }
  const moved = shapes.map((shape) => shape.map((point) => point.clone()));
  for (const arc of ARCS) {
    const path = curve(arc);
    let free = true;
    for (let i = 1; i < samples && free; i++) {
      path(i / samples, at);
      const shape = shapes[i - 1]!;
      const here = moved[i - 1]!;
      for (let p = 0; p < shape.length; p++) here[p]!.copy(shape[p]!).add(at);
      free = clearance.free(here, allowed);
    }
    if (free) return path;
  }
  return curve(ARCS.at(-1)!);
}

/**
 * How a selected device may be shown where it stands: lifted by `up`, it
 * turns SWAY radians to either side. Returns how far out the way it faces it
 * must be held for that turn to touch nothing it does not touch at rest, and
 * the turn itself, which is narrowed only when no distance within reach is
 * enough.
 */
export function clearTurn(seat: Seat, up: number, clearance: Clearance | null): { out: number; sway: number } {
  if (!clearance) return { out: 0, sway: SWAY };
  const allowed = resting(seat, clearance);
  const held = (out: number, turn: number) =>
    clearance.free(standing(seat.box, seat.position, seat.yaw + turn, new THREE.Vector3(0, up, out)), allowed);
  // How far out the device can be carried at all, facing home.
  let reach = 0;
  while (reach + 0.02 <= 0.3001 && held(reach + 0.02, 0)) reach += 0.02;
  for (const sway of [SWAY, SWAY * 0.7, SWAY * 0.4]) {
    for (let out = 0; out <= reach + 1e-9; out += 0.02) {
      if ([-1, -0.5, 0.5, 1].every((side) => held(out, side * sway))) return { out, sway };
    }
  }
  return { out: 0, sway: 0 };
}
